import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  requestOtp,
  loginRegisterVerifyOtp,
  fetchUserAddresses,
  fetchUserDetails,
  addAddress,
  updateUser,
  updateAddress,
  getUserCurrentLocation,
  VerifyOtpAfterLogin
} from "../api/AuthApi.services";
import CountryStateCitySelector from "./CountryStateCitySelector";
import { useNavigate } from "react-router-dom";
import { Country, State, City } from "country-state-city";

const AuthModal = ({ show, onClose, onLoginSuccess, redirectFrom, onCloseModel }) => {
  const [modalStep, setModalStep] = useState("LOGIN");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [address, setAddress] = useState({ country: "", state: "", city: "" });
  const [resendDisabled, setResendDisabled] = useState(false);
  const [isCreated, setIsCreated] = useState(false);
  const navigate = useNavigate();
  const [isMobileReadOnly, setIsMobileReadOnly] = useState(false);
  const [isEmailReadOnly, setIsEmailReadOnly] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false); 
  const [phoneOtpSent, setPhoneOtpSent] = useState(false); 
  const [emailVerified, setEmailVerified] = useState(false); 
  const [phoneVerified, setPhoneVerified] = useState(false); 
  const [emailOtpError, setEmailOtpError] = useState(""); 
  const [phoneOtpError, setPhoneOtpError] = useState(""); 
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const { register: registerRegister, handleSubmit: handleRegisterSubmit, setValue, watch, reset: resetRegisterForm, formState: { errors: registerErrors } } = useForm();

  useEffect(() => {
    const storedUserDetails = localStorage.getItem("userDetails");
    if (storedUserDetails) {
      setUserDetails(JSON.parse(storedUserDetails));
      setModalStep("PROFILE");
    } else {
      setModalStep("LOGIN");
    }
  }, [show]);

  // Reset errors and states when modal is closed
  useEffect(() => {
    if (!show) {
      // Reset form errors
      reset();
      resetRegisterForm();
      
      // Reset custom error states
      setOtpError("");
      setEmailOtpError("");
      setPhoneOtpError("");
      
      // Reset OTP states
      setEmailOtpSent(false);
      setPhoneOtpSent(false);
      setEmailVerified(false);
      setPhoneVerified(false);
      
      // Reset input states
      setEmail("");
      setPhone("");
      setIsCreated(false);
      setResendDisabled(false);
      setModalStep("LOGIN");
    }
  }, [show, reset, resetRegisterForm]);

  const myOrders = () => {
    navigate(`/orders`);
  };

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
  
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const response = await getUserCurrentLocation({ latitude, longitude });
  
          if (response?.address) {
            console.log("📌 Raw Address Response:", response.address);
  
            const { country_code, state, city, postcode } = response.address;
  
            const countryData = Country.getAllCountries().find(
              (c) => c.isoCode.toLowerCase() === country_code.toLowerCase()
            );
  
            const fullCountryName = countryData ? countryData.name : "";
  
            const countryStates = State.getStatesOfCountry(country_code.toUpperCase());
            const matchedState = countryStates.find(
              (s) => s.name.toLowerCase() === state.toLowerCase() || s.isoCode.toLowerCase() === state.toLowerCase()
            );
  
            const fullStateName = matchedState ? matchedState.name : "";
            const stateIsoCode = matchedState ? matchedState.isoCode : "";
  
            const stateCities = matchedState
              ? City.getCitiesOfState(country_code.toUpperCase(), stateIsoCode)
              : [];
  
            const matchedCity = stateCities.find(
              (c) => c.name.toLowerCase().includes(city.toLowerCase())
            );
  
            const fullCityName = matchedCity ? matchedCity.name : "";
  
            console.log("✅ Extracted Location Data:", {
              country: fullCountryName,
              state: fullStateName,
              city: fullCityName,
              pincode: postcode,
            });
  
            const newAddress = {
              country: fullCountryName || "",
              state: fullStateName || "",
              city: fullCityName || "",
              pincode: postcode || "",
            };
  
            setAddress(newAddress);
            handleLocationChange(newAddress);
  
            setTimeout(() => {
              setValue("country", newAddress.country);
              setValue("state", newAddress.state);
              setValue("city", newAddress.city);
              setValue("pincode", newAddress.pincode);
            }, 100);
          } else {
            alert("Unable to fetch address from location.");
          }
        } catch (error) {
          console.error("❌ Error fetching location:", error);
          alert("Failed to fetch location details. Please try again.");
        }
      },
      (error) => {
        console.error("❌ Error getting geolocation:", error);
        alert("Unable to retrieve your location.");
      }
    );
  };

  const handleLocationChange = (newAddress) => {
    setAddress(newAddress);
    setValue("country", newAddress.country);
    setValue("state", newAddress.state);
    setValue("city", newAddress.city);
  };

  const handleSendOtp = async (data) => {
    try {
      let inputType = "email";
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[0-9]{10}$/;
      if (emailRegex.test(data.loginInput)) {
        inputType = "email";
      } else if (phoneRegex.test(data.loginInput)) {
        inputType = "phone";
      }
      const payload = inputType === "email" ? { email: data.loginInput } : { phone_number: data.loginInput };
      const response = await requestOtp(payload);
      setEmail(inputType === "email" ? data.loginInput : "");
      setPhone(inputType === "phone" ? data.loginInput : "");
      if (response?.data?.["user-created"] !== undefined) {
        setIsCreated(response.data["user-created"]);
      }
      console.log("OTP request response:", response);
      reset();
      if(response?.status === 200){
        setModalStep("VERIFY_OTP");
      }

    } catch (error) {
      console.error("Failed to request OTP:", error);
    }
  };

  const handleSendPhoneOtp = async (phoneValue) => {
    try {
      const payload = { phone_number: phoneValue };
      const response = await updateUser(payload);
  
      if (!response.success) {
        let errorMessage = "Failed to send OTP. Please try again.";
        try {
          const errorObj = JSON.parse(response.error.split(": ")[1]);
          errorMessage = errorObj.message || errorMessage;
        } catch (parseError) {
          console.error("Failed to parse error message:", parseError);
          errorMessage = response.error || errorMessage;
        }
        throw new Error(errorMessage);
      }
  
      console.log("Phone OTP sent successfully:", response);
      setPhoneOtpSent(true);
      setPhoneOtpError("");
    } catch (error) {
      console.error("Failed to send phone OTP:", error);
      const errorMessage = error.message || "Failed to send OTP. Please try again.";
      setPhoneOtpError(errorMessage);
    }
  };
  
  const handleSendEmailOtp = async (emailValue) => {
    try {
      const payload = { email: emailValue };
      console.log("Sending OTP payload:", payload);
      const response = await updateUser(payload); 
  
      if (!response.success) {
        let errorMessage = "Failed to send OTP. Please try again.";
        try {
          const errorObj = JSON.parse(response.error.split(": ")[1]);
          errorMessage = errorObj.message || errorMessage;
        } catch (parseError) {
          console.error("Failed to parse error message:", parseError);
          errorMessage = response.error || errorMessage;
        }
        throw new Error(errorMessage);
      }
  
      console.log("Email OTP sent successfully:", response);
      setEmailOtpSent(true);
      setEmailOtpError("");
    } catch (error) {
      console.error("Failed to send email OTP:", error);
      const errorMessage = error.message || "Failed to send OTP. Please try again.";
      setEmailOtpError(errorMessage);
    }
  };

  const handleVerifyPhoneOtp = async (otp) => {
    try {
      const payload = {
        channel: "phone",
        otp_code: otp,
      };
      console.log("Verifying OTP payload:", payload);
      const response = await VerifyOtpAfterLogin(payload);
      console.log("Phone OTP verification response:", response);
      if (response && response.success) {
        console.log("Phone OTP verified successfully:", response);
        setPhoneVerified(true);
        setPhoneOtpSent(false);
        setPhoneOtpError("");
        setUserDetails((prev) => ({ ...prev, phone_number_verified: true }));
      } else {
        throw new Error("Invalid OTP");
      }
    } catch (error) {
      console.error("Failed to verify phone OTP:", error);
      setPhoneOtpError("Invalid OTP. Please try again.");
    }
  };

  const handleVerifyEmailOtp = async (otp) => {
    try {
      const payload = {
        channel: "email",
        otp_code: otp,
      };
      console.log("Verifying OTP payload:", payload);
      const response = await VerifyOtpAfterLogin(payload);
      if (response && response.success) {
        console.log("Email OTP verified successfully:", response);
        setEmailVerified(true);
        setEmailOtpSent(false);
        setEmailOtpError("");
        setUserDetails((prev) => ({ ...prev, email_verified: true }));
      } else {
        throw new Error("Invalid OTP");
      }
    } catch (error) {
      console.error("Failed to verify email OTP:", error);
      setEmailOtpError("Invalid OTP. Please try again.");
    }
  };

  const handleVerifyOtp = async (data) => {
    try {
      let inputType = "email";
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[0-9]{10}$/;
      if (emailRegex.test(email)) {
        inputType = "email";
      } else if (phoneRegex.test(phone)) {
        inputType = "phone";
      }
      const payload = {
        [inputType === "email" ? "email" : "phone_number"]: inputType === "email" ? email : phone,
        otp: data.otp,
        guestUserId: localStorage.getItem("guest_user_id"),
      };
      const response = await loginRegisterVerifyOtp(payload);
  
      if (response && response.tokens) {
        localStorage.setItem("accessToken", response.tokens.access);
        localStorage.setItem("refreshToken", response.tokens.refresh);
        localStorage.removeItem("guest_user_id");
  
        console.log("OTP verified successfully. Tokens saved and guest_user_id cleared:", response);
  
        if (isCreated) {
          setModalStep("REGISTER");
        } else {
          const userAddresses = await fetchUserAddresses();
          const userDetails = await fetchUserDetails();
  
          if (Array.isArray(userAddresses) && userAddresses.length === 0) {
            setModalStep("REGISTER");
            setValue("mobile", userDetails?.phone_number || "");
            setValue("userName", userDetails.username || "");
            setValue("email", userDetails.email || "");
            setIsMobileReadOnly(!!userDetails.phone_number);
            setPhoneVerified(!!userDetails.phone_number);
            setIsEmailReadOnly(!!userDetails.email);
            setEmailVerified(!!userDetails.email);
          } else {
            const currentAddresses = userAddresses.filter((address) => address.is_current === true);
            console.log("currentAddress", currentAddresses);
  
            const userData = {
              username: userDetails?.username || null,
              email: userDetails?.email || null,
              phone_number: userDetails?.phone_number || null,
              addressId: currentAddresses[0]?.id || null,
              address: currentAddresses[0]?.address_line_1 || null,
              address_type: currentAddresses[0]?.address_type || null,
              city: currentAddresses[0]?.city || null,
              postalCode: currentAddresses[0]?.postal_code || null,
              state: currentAddresses[0]?.state || null,
              country: currentAddresses[0]?.country || null,
            };
  
            setUserDetails(userData);
            localStorage.setItem("userDetails", JSON.stringify(userData));
            if (redirectFrom === "cartPage") {
              onClose();
            } else {
              setModalStep("PROFILE");
            }
          }
          console.log("userAddresses", userAddresses, "userDetails", userDetails);
        }
        reset();
        setOtpError("");
      } else {
        throw new Error("Invalid OTP");
      }
    } catch (error) {
      setOtpError("Invalid OTP. Please try again.");
    }
  };

  const handleResendOtp = async () => {
    try {
      setResendDisabled(true);
      const response = await requestOtp({ email });
      console.log("OTP resent successfully:", response);
  
      setTimeout(() => setResendDisabled(false), 30000);
  
      setOtpError("");
    } catch (error) {
      console.error("Failed to resend OTP:", error);
      setOtpError("Failed to resend OTP. Please try again later.");
    }
  };

  const handleRegister = async (formData) => {
    try {
      const addressPayload = {
        city: formData.city,
        address_line_1: formData.address,
        state: formData.state,
        country: formData.country,
        postal_code: formData.pincode,
        address_type: formData.address_type,
      };
  
      const userPayload = {
        username: formData.userName, 
      };
      
      if (formData.mobile) {
        userPayload.mobile = formData.mobile;
      }
  
      let userResult;
      let addressResult;
  
      const hasUserDetails = userDetails && userDetails.addressId;
  
      if (isEditing && hasUserDetails) {
        addressResult = await updateAddress(userDetails.addressId, addressPayload);
        userResult = await updateUser(userPayload);
        setIsEditing(false);
      } else {
        addressResult = await addAddress(addressPayload);
        userResult = await updateUser(userPayload);
      }
  
      if (!addressResult.success) {
        console.error("Address addition failed:", addressResult.error);
        console.log("userResult", userResult);
        return;
      }
      const userInfo = await fetchUserDetails();
  
      const userData = {
        username: userInfo?.username || null,
        email: userInfo?.email || null,
        phone_number: userInfo?.phone_number || null,
        addressId: addressResult?.data?.id || null,
        address: addressResult?.data?.address_line_1 || null,
        address_type: addressResult?.data?.address_type || null,
        city: addressResult?.data?.city || null,
        postalCode: addressResult?.data?.postal_code || null,
        state: addressResult?.data?.state || null,
        country: addressResult?.data?.country || null,
      };
  
      setUserDetails(userData);
      localStorage.setItem("userDetails", JSON.stringify(userData));
      if (redirectFrom === "cartPage") { 
        onClose();
      } else {
        setModalStep("PROFILE");
      }
  
      resetRegisterForm();
  
      console.log(" Address added successfully:", addressResult.data);
    } catch (error) {
      console.error(" Error in registration process:", error);
      resetRegisterForm();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userDetails");
    setUserDetails({});
    navigate("/");
    setModalStep("LOGIN");
    onClose();
  };

  const editProfile = () => {
    const storedUserDetails = localStorage.getItem("userDetails");
  
    const parsedUserDetails = storedUserDetails ? JSON.parse(storedUserDetails) : null;
  
    if (parsedUserDetails) {
      console.log("📌 User Details Loaded for Editing:", parsedUserDetails);
  
      setValue("userName", parsedUserDetails.username || "");
      setValue("email", parsedUserDetails.email || "");
      setValue("mobile", parsedUserDetails.phone_number || "");
      setValue("address", parsedUserDetails.address || "");
      setValue("pincode", parsedUserDetails.postalCode || "");
      setValue("address_type", parsedUserDetails.address_type || "");
  
      setIsMobileReadOnly(!!parsedUserDetails.phone_number);
      setIsEmailReadOnly(!!parsedUserDetails.email);
  
      const countryData = Country.getAllCountries().find(
        (c) => c.name.toLowerCase() === parsedUserDetails.country.toLowerCase()
      );
      const countryIsoCode = countryData ? countryData.isoCode : "";
  
      let matchedState = null;
      let stateIsoCode = "";
  
      if (countryIsoCode) {
        const countryStates = State.getStatesOfCountry(countryIsoCode);
        matchedState = countryStates.find(
          (s) => s.name.toLowerCase().trim() === parsedUserDetails.state.toLowerCase().trim()
        );
        stateIsoCode = matchedState ? matchedState.isoCode : "";
      }
  
      let matchedCity = null;
  
      if (stateIsoCode) {
        const stateCities = City.getCitiesOfState(countryIsoCode, stateIsoCode);
        matchedCity = stateCities.find(
          (c) => c.name.toLowerCase().trim() === parsedUserDetails.city.toLowerCase().trim()
        );
      }
  
      console.log("Extracted Country ISO:", countryIsoCode);
      console.log("Matched State:", matchedState);
      console.log("Matched City:", matchedCity);
  
      setValue("country", parsedUserDetails.country);
      setValue("state", matchedState ? matchedState.name : "");
      setValue("city", matchedCity ? matchedCity.name : "");
  
      handleLocationChange({
        country: parsedUserDetails.country,
        state: matchedState ? matchedState.name : "",
        city: matchedCity ? matchedCity.name : "",
      });
  
      setAddress({
        country: parsedUserDetails.country,
        state: matchedState ? matchedState.name : "",
        city: matchedCity ? matchedCity.name : "",
      });
    }
  
    setIsEditing(true);
    setModalStep("REGISTER");
  };

  return show ? (
    <div className="modal-overlay">
      <div className="profile-modal">
        <button className="btn-close" aria-label="Close" onClick={onCloseModel}></button>

        {modalStep === "LOGIN" && (
          <form onSubmit={handleSubmit(handleSendOtp)}>
            <h4>Login/Register</h4>
            <div className="mb-3">
              <input
                type="text"
                id="loginInput"
                placeholder="Enter Email or Phone Number"
                {...register("loginInput", {
                  required: "Email or phone number is required",
                  validate: (value) => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const phoneRegex = /^[0-9]{10}$/;
                    console.log("Validating input:", value); 
                    if (emailRegex.test(value)) {
                      setEmail(value);
                      setPhone(""); 
                      return true;
                    } else if (phoneRegex.test(value)) {
                      setPhone(value);
                      setEmail("");
                      return true;
                    }
                    return "Invalid email or phone number. Use a valid email or 10-digit phone number.";
                  },
                })}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "10px",
                  border: errors.loginInput ? "1px solid red" : "1px solid #ddd",
                  borderRadius: "5px",
                }}
              />
              {errors.loginInput && <p className="text-danger">{errors.loginInput.message}</p>}
            </div>
            <button type="submit" className="btn btn-primary w-100">Send OTP</button>
          </form>
        )}

        {modalStep === "VERIFY_OTP" && (
          <div style={{ marginBottom: "15px", marginTop:"30px" }}>
            <form onSubmit={handleSubmit(handleVerifyOtp)}>
              <input
                type="number"
                placeholder="Enter OTP"
                {...register("otp", { 
                  required: "OTP is required",
                  minLength: { value: 6, message: "OTP must be 6 digits" },
                  maxLength: { value: 6, message: "OTP must be 6 digits" },
                  pattern: { value: /^\d{6}$/, message: "OTP must be numeric" }
                })} 
                style={{
                  width: "100%",
                  padding: "10px",
                  marginBottom: "10px",
                  border: errors.otp || otpError ? "1px solid red" : "1px solid #ddd",
                  borderRadius: "5px",
                }}
              />
              {errors.otp && <p className="text-danger">{errors.otp.message}</p>}
              {otpError && <p className="text-danger">{otpError}</p>}
              <button type="submit" className="btn btn-primary w-100">Verify OTP</button>
            </form>
                      
            <div style={{ marginTop: "10px", textAlign: "center" }}>
              <p>Didn't receive the OTP?</p>
              <button
                className="btn btn-secondary"
                onClick={handleResendOtp}
                disabled={resendDisabled}
              >
                {resendDisabled ? "Resend OTP in 30s" : "Resend OTP"}
              </button>
            </div>
          </div>
        )}

        {modalStep === "REGISTER" && (
          <>
            <div className="mx-auto">
              <div className="rounded bg-white">
                <h5 className="mb-4">Enter your details</h5>
                <div className="form-container">
                  <form onSubmit={handleRegisterSubmit(handleRegister)}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        placeholder="Name"
                        {...registerRegister("userName", { 
                          required: "Name is required",
                          minLength: { value: 2, message: "Name must be at least 2 characters long" }
                        })}
                        style={{
                          border: registerErrors.userName ? "1px solid red" : "1px solid #ddd",
                        }}
                      />
                      {registerErrors.userName && <p className="text-danger">{registerErrors.userName.message}</p>}
                    </div>

                    {/* Email Field with OTP Verification */}
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                      <div className="position-relative">
                        {userDetails?.email_verified || emailVerified ? (
                          <div className="position-relative">
                            <input
                              type="text"
                              placeholder="Email"
                              readOnly
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 40px 10px 10px",
                                marginBottom: "10px",
                              }}
                              {...registerRegister("email", {
                                required: "Email is required",
                                pattern: {
                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                  message: "Invalid email address",
                                },
                              })}
                            />
                            <span
                              className="material-icons position-absolute"
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "green",
                                fontSize: "24px",
                              }}
                              title="Verified"
                            >
                              check_circle
                            </span>
                          </div>
                        ) : (
                          <div className="position-relative">
                            <input
                              type="text"
                              placeholder="Email"
                              readOnly={isEmailReadOnly || emailVerified}
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: `10px ${emailOtpSent ? "45%" : "45%"} 10px 10px`,
                                marginBottom: "10px",
                                border: registerErrors.email || emailOtpError ? "1px solid red" : "1px solid #ddd",
                              }}
                              {...registerRegister("email", {
                                required: "Email is required",
                                pattern: {
                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                  message: "Invalid email address",
                                },
                              })}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailOtpSent(false);
                                setEmailVerified(false);
                              }}
                            />
                            {emailOtpSent ? (
                              <div
                                className="position-absolute"
                                style={{
                                  right: "0px",
                                  top: "0%",
                                  width: "45%",
                                  display: "flex",
                                  alignItems: "center",
                                  height: "45px",
                                }}
                              >
                                <input
                                  type="number"
                                  placeholder="OTP"
                                  className="form-control"
                                  style={{
                                    width: "60%",
                                    padding: "5px",
                                    fontSize: "14px",
                                    marginRight: "0px",
                                    height: "45px",
                                    marginBottom: "0px",
                                    border: registerErrors.emailOtp ? "1px solid red" : "1px solid #ddd",
                                  }}
                                  {...registerRegister("emailOtp", {
                                    required: "OTP is required",
                                    pattern: {
                                      value: /^\d{6}$/,
                                      message: "Enter a valid 6-digit OTP",
                                    },
                                  })}
                                />
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  style={{
                                    width: "40%",
                                    padding: "5px 0px",
                                    fontSize: "14px",
                                    height: "45px",
                                  }}
                                  onClick={() => handleVerifyEmailOtp(watch("emailOtp"))}
                                >
                                  Verify OTP
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-secondary position-absolute"
                                style={{
                                  right: "0px",
                                  top: "0%",
                                  width: "35%",
                                  padding: "10px",
                                  fontSize: "14px",
                                  height: "45px",
                                  backgroundColor: "#0d6efd"
                                }}
                                onClick={() => handleSendEmailOtp(watch("email"))}
                                disabled={isEmailReadOnly || emailVerified}
                              >
                                Verify
                              </button>
                            )}
                            {registerErrors.email && <p className="text-danger">{registerErrors.email.message}</p>}
                            {registerErrors.emailOtp && <p className="text-danger">{registerErrors.emailOtp.message}</p>}
                            {emailOtpError && <p className="text-danger">{emailOtpError}</p>}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Mobile Number Field with OTP Verification */}
                    <div className="mb-3">
                      <label htmlFor="mobile" className="form-label">
                        Mobile number
                      </label>
                      <div className="position-relative">
                        {userDetails?.phone_number_verified || phoneVerified ? (
                          <div className="position-relative">
                            <input
                              type="tel"
                              placeholder="Mobile number"
                              readOnly
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: "10px 40px 10px 10px",
                                marginBottom: "10px",
                              }}
                              {...registerRegister("mobile", {
                                required: "Mobile number is required",
                                pattern: {
                                  value: /^[0-9]{10}$/,
                                  message: "Invalid mobile number",
                                },
                              })}
                            />
                            <span
                              className="material-icons position-absolute"
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "green",
                                fontSize: "24px",
                              }}
                              title="Verified"
                            >
                              check_circle
                            </span>
                          </div>
                        ) : (
                          <div className="position-relative">
                            <input
                              type="tel"
                              placeholder="Mobile number"
                              readOnly={isMobileReadOnly || phoneVerified}
                              className="form-control"
                              style={{
                                width: "100%",
                                padding: `10px ${phoneOtpSent ? "45%" : "45%"} 10px 10px`,
                                marginBottom: "10px",
                                border: registerErrors.mobile || phoneOtpError ? "1px solid red" : "1px solid #ddd",
                              }}
                              {...registerRegister("mobile", {
                                required: "Mobile number is required",
                                pattern: {
                                  value: /^[0-9]{10}$/,
                                  message: "Invalid mobile number",
                                },
                              })}
                              onChange={(e) => {
                                setPhone(e.target.value);
                                setPhoneOtpSent(false);
                                setPhoneVerified(false);
                              }}
                            />
                            {phoneOtpSent ? (
                              <div
                                className="position-absolute"
                                style={{
                                  right: "0px",
                                  top: "0%",
                                  width: "45%",
                                  display: "flex",
                                  alignItems: "center",
                                  height: "45px",
                                }}
                              >
                                <input
                                  type="number"
                                  placeholder="OTP"
                                  className="form-control"
                                  style={{
                                    width: "60%",
                                    padding: "5px",
                                    fontSize: "14px",
                                    marginRight: "0px",
                                    height: "45px",
                                    marginBottom: "0px",
                                    border: registerErrors.phoneOtp ? "1px solid red" : "1px solid #ddd",
                                  }}
                                  {...registerRegister("phoneOtp", {
                                    required: "OTP is required",
                                    pattern: {
                                      value: /^\d{6}$/,
                                      message: "Enter a valid 6-digit OTP",
                                    },
                                  })}
                                />
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  style={{
                                    width: "40%",
                                    padding: "5px 0px",
                                    fontSize: "14px",
                                    height: "45px",
                                  }}
                                  onClick={() => handleVerifyPhoneOtp(watch("phoneOtp"))}
                                >
                                  Verify OTP
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-secondary position-absolute"
                                style={{
                                  right: "0px",
                                  top: "0%",
                                  width: "35%",
                                  padding: "10px",
                                  fontSize: "14px",
                                  height: "45px",
                                  backgroundColor: "#0d6efd"
                                }}
                                onClick={() => {
                                  handleSendPhoneOtp(watch("mobile"));
                                }}
                                disabled={isMobileReadOnly || phoneVerified}
                              >
                                Verify
                              </button>
                            )}
                            {registerErrors.mobile && <p className="text-danger">{registerErrors.mobile.message}</p>}
                            {registerErrors.phoneOtp && <p className="text-danger">{registerErrors.phoneOtp.message}</p>}
                            {phoneOtpError && <p className="text-danger">{phoneOtpError}</p>}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="address" className="form-label">
                        Address (Area and Street)
                      </label>
                      <button
                        type="button"
                        className="btn btn-primary m-0 p-0 px-2 float-end size-getcurrent-button"
                        onClick={getCurrentLocation}
                      >
                        <span
                          className="material-icons me-2"
                          style={{ color: "#FFF", fontSize: "14px" }}
                        >
                          my_location
                        </span>
                        Current Location
                      </button>
                      <textarea
                        className="form-control"
                        id="address"
                        rows="2"
                        placeholder="Address (Area and Street)"
                        {...registerRegister("address", { 
                          required: "Address is required",
                          minLength: { value: 5, message: "Address must be at least 5 characters long" }
                        })}
                        style={{
                          border: registerErrors.address ? "1px solid red" : "1px solid #ddd",
                        }}
                      ></textarea>
                      {registerErrors.address && <p className="text-danger">{registerErrors.address.message}</p>}
                    </div>

                    {/* Country-State-City Selector */}
                    <CountryStateCitySelector
                      onChange={handleLocationChange}
                      countryValue={address.country}
                      stateValue={address.state}
                      cityValue={address.city}
                    />

                    <div className="mb-3">
                      <label htmlFor="pincode" className="form-label">
                        Pincode
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="pincode"
                        placeholder="Pincode"
                        {...registerRegister("pincode", {
                          required: "Pincode is required",
                          pattern: {
                            value: /^[1-9][0-9]{5}$/,
                            message: "Invalid pincode",
                          },
                        })}
                        style={{
                          border: registerErrors.pincode ? "1px solid red" : "1px solid #ddd",
                        }}
                      />
                      {registerErrors.pincode && <p className="text-danger">{registerErrors.pincode.message}</p>}
                    </div>

                    <div className="mb-3 d-flex align-items-center">
                      <label className="form-label me-3 mb-0">Address Type:</label>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="address_type"
                          id="home"
                          value="home"
                          {...registerRegister("address_type", { required: "Please select an address type" })}
                          checked={watch("address_type") === "home"}
                        />
                        <label className="form-check-label" htmlFor="home">
                          Home
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="address_type"
                          id="work"
                          value="work"
                          {...registerRegister("address_type", { required: "Please select an address type" })}
                          checked={watch("address_type") === "work"}
                        />
                        <label className="form-check-label" htmlFor="work">
                          Work
                        </label>
                      </div>
                      {registerErrors.address_type && (
                        <p className="text-danger">{registerErrors.address_type.message}</p>
                      )}
                    </div>

                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-warning w-25 me-2"
                        onClick={() => {
                          setModalStep("PROFILE");
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary w-25">
                        Save
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </>
        )}

        {modalStep === "PROFILE" && (
          <>
            <div className="">
              <div className="d-flex flex-sm-row flex-column justify-content-evenly align-items-center mt-3 gap-2"> 
                  <button 
                    className="btn btn-custom-login me-2 btn-sm px-2 py-1 text-center d-flex align-items-center justify-content-center"
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    title="Order Details"
                    onClick={myOrders}
                  >
                    <span className="material-icons me-2">receipt_long</span> 
                    My Orders
                  </button>

                  <button 
                    className="btn btn-custom-login me-2 btn-sm px-2 py-1 text-center d-flex align-items-center justify-content-center" 
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    title="Edit Profile"
                    onClick={editProfile}
                  >
                    <span className="material-icons me-2" style={{ fontSize: "24px" }}>edit</span> Edit 
                  </button>

                  <button 
                    className="btn btn-custom-login me-2 btn-sm px-2 py-1 text-center d-flex align-items-center justify-content-center" 
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top" 
                    title="Logout"
                    onClick={handleLogout}
                  >
                    <span className="material-icons me-2">logout</span> Logout
                  </button>
              </div>

              <div className="form-container mt-3">
                <div className="profile-card">
                  <div className="info-card">
                    <p className="info-label-login">Name:</p>
                    <p className="info-value-login">{userDetails?.username || ""}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">Email:</p>
                    <p className="info-value-login">{userDetails?.email || ""}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">Mobile Number:</p>
                    <p className="info-value-login">{userDetails?.mobileNumber || ""}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">Address:</p>
                    <p className="info-value-login">{userDetails?.address || ""}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">Country:</p>
                    <p className="info-value-login">{userDetails?.country || ""}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">State:</p>
                    <p className="info-value-login">{userDetails?.state || ""}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">City:</p>
                    <p className="info-value-login">{userDetails?.city || " "}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">Pincode:</p>
                    <p className="info-value-login">{userDetails?.postalCode || " "}</p>
                  </div>
                  <div className="info-card">
                    <p className="info-label-login">Address Type:</p>
                    <p className="info-value-login">{userDetails?.address_type || " "}</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;
};

export default AuthModal;